import { createContext, ReactNode, useCallback, useContext } from 'react';
import { toast } from 'sonner';

type Severity = 'success' | 'error' | 'warning' | 'info';

interface NotificationContextType {
  showNotification: (message: string, severity?: Severity, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  closeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const showNotification = useCallback(
    (message: string, severity: Severity = 'info', duration: number = 5000) => {
      // Dedup nativo do sonner: passar `id` derivado da severidade + mensagem
      // colapsa toasts idênticos disparados em sequência (ex.: dois onError de
      // mutations duplicadas, ou um effect que reroda em StrictMode). Mensagens
      // diferentes (mesmo de mesma severidade) continuam empilhando.
      const id = `${severity}:${message}`;
      const options = { duration, id };

      switch (severity) {
        case 'success':
          toast.success(message, options);
          break;
        case 'error':
          toast.error(message, options);
          break;
        case 'warning':
          toast.warning(message, options);
          break;
        case 'info':
          toast.info(message, options);
          break;
      }
    },
    []
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => showNotification(message, 'success', duration),
    [showNotification]
  );

  const showError = useCallback(
    (message: string, duration?: number) => showNotification(message, 'error', duration || 7000),
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => showNotification(message, 'warning', duration),
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => showNotification(message, 'info', duration),
    [showNotification]
  );

  const closeNotification = useCallback((id: string) => {
    toast.dismiss(id);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        closeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationProvider;
